import { Injectable } from '@angular/core';
import { FormActionHandlerService } from './base.service';
import { FormShareService } from '../share.service';
import { UserContextService } from '../../../user/services/user-context.service';
import { ProjectDataService } from '../../../project/services/project-data.service';
import { SwalService } from '../../../fastlane-common/services/swal.service';
import { formStatuses, formTypes } from '../../form.constants';
import { FormIdentifier } from '../../../project/models/form-identifier.model';
import { StatusLog } from '../../../project/models/status-log.model';
import { CastProject } from '../../../project/project.functions';
import { EmailService } from '../../../email/services/email.service';
import { SweetAlertOptions } from 'sweetalert2';

/**
 * Footer "Send Examination" action (FormAction `send-desk-audit`) for the RD application forms
 * (LQRE / Increase in R&D / SBIR).
 *
 * This creates the project's Examination (rdAudit), sets it to Information Required (so the
 * applicant can complete it), prompts the manager for comments (recorded on the Examination's
 * status-log entry), and persists both the new Examination and the status change in a single
 * save. It then notifies the business signatory with a dedicated "detailed examination
 * requested" letter (EmailService.sendRdExaminationRequestedEmail) INSTEAD of the standard
 * form-status-change email.
 *
 * The footer button is disabled once an Examination already exists (see FormActionsComponent),
 * so this handler only ever creates the first/only Examination.
 */
@Injectable()
export class SendDeskAuditActionHandlerService extends FormActionHandlerService {
  constructor(
    private shareService: FormShareService,
    private userContext: UserContextService,
    private projectService: ProjectDataService,
    private swal: SwalService,
    private emailService: EmailService
  ) {
    super();
  }

  exec(): Promise<any> {
    const that = this;
    const formIdentifier = this.shareService.formId;

    const swalOptions = <SweetAlertOptions>{
      title: 'Create the Examination and request more information from the applicant?',
      inputPlaceholder:
        'Enter your comments here. These comments will be included in the email to the applicant and saved to the Examination\'s status history.',
      type: 'question',
      confirmButtonText: 'Yes, Send Examination',
      allowOutsideClick: false
    };
    const swalEmptyInputErrorMessage =
      'Comments must be entered in order to proceed.';

    return new Promise((resolve, reject) => {
      that.swal
        .textarea(swalOptions, 'required', swalEmptyInputErrorMessage)
        .then(comments => {
          that.swal.load('Creating Examination...');

          that.projectService.getProject(formIdentifier.projectGuid).subscribe(
            project => {
              const prototypeProject = CastProject(
                project,
                project.projectInfo.incentiveProgram
              );
              Object.assign(prototypeProject, project);
              prototypeProject.init();

              const deskAuditFormId = <FormIdentifier>{
                formType: formTypes.rdAudit.abbrev,
                formIndex: 0,
                projectGuid: formIdentifier.projectGuid
              };

              // Create the Examination and set form to Information Required so the
              // applicant can complete it (rdAudit is applicant-editable only in Information
              // Required). getProjectForm news up an AuditApplication (default status Pending)
              // and assigns it to prototypeProject.rdAudit when none exists.
              const newFormStatus = <StatusLog>{
                status: formStatuses.informationRequired.name,
                statusDate: new Date(),
                comment: comments,
                creator: that.userContext.currentUser.id
              };
              const protoDeskAudit = prototypeProject.getProjectForm(deskAuditFormId);
              protoDeskAudit.statuses.unshift(newFormStatus);
              protoDeskAudit.modifiedDate = new Date(Date.now());

              // Persist the new Examination (in Information Required).
              that.projectService.updateProject(prototypeProject).subscribe(
                () => {
                  // Reflect the new Examination on the in-memory project so the footer button
                  // disables (an Examination now exists). shareService.project is the same object
                  // the viewer reads.
                  const deskAudit = that.shareService.project.getProjectForm(deskAuditFormId);
                  deskAudit.statuses.unshift(newFormStatus);

                  that.emailService
                    .sendRdExaminationRequestedEmail(
                      that.shareService.project,
                      formIdentifier.projectGuid,
                      comments
                    )
                    .subscribe(
                      () => {
                        that.swal
                          .success({
                            title: 'Examination created and set to Information Required.',
                            text: 'The applicant has been notified.'
                          })
                          .then(() => resolve())
                          .catch(() => resolve());
                      },
                      () => {
                        that.swal
                          .error({
                            title:
                              'Examination created, but the notification email could not be sent.',
                            text:
                              'The Examination is in Information Required. Please try notifying the applicant again.'
                          })
                          .then(() => resolve())
                          .catch(() => resolve());
                      }
                    );
                },
                error => {
                  if (error === 409) {
                    reject(error);
                  } else {
                    reject('An error occurred while creating the Examination.');
                  }
                }
              );
            },
            () => reject('An error occurred while loading the project.')
          );
        })
        .catch(() => resolve());
    });
  }
}
